import { useTheme } from '../../../../theme';
import type { IconName } from '@doxyme/icons';
import { Sm, Md, Lg, Xl, _2XL, _3XL } from './statusBadge.variants';
import { type FC } from 'react';

export type StatusBadgeSize =
  | '3xl'
  | '2xl' // 32px
  | 'xl' // 24px
  | 'lg' // 20px
  | 'md' // 16px
  | 'sm'; // 8px

export type StatusBadgeColor =
  | 'positive'
  | 'negative'
  | 'notice'
  | 'informative'
  | 'neutral';

export type StatusBadgeProps = {
  color: StatusBadgeColor;
  size: StatusBadgeSize;
  className?: string;
  hasBorder?: boolean;
  iconName?: IconName;
};

const getStatusBadgeColor = (
  type: StatusBadgeColor,
  theme: ReturnType<typeof useTheme>,
): string => {
  const colorMap: Record<StatusBadgeColor, string> = {
    positive: theme.status.positive,
    negative: theme.status.negative,
    notice: theme.status.notice,
    informative: theme.status.informative,
    neutral: theme.status.grey,
  };

  return colorMap[type] || theme.status.grey;
};

const variantsToSizeMap: Record<StatusBadgeSize, typeof Lg> = {
  '3xl': _3XL,
  '2xl': _2XL,
  xl: Xl,
  lg: Lg,
  md: Md,
  sm: Sm,
};

export const StatusBadge: FC<StatusBadgeProps> = ({
  color,
  size,
  className,
  hasBorder = false,
  iconName,
}) => {
  const theme = useTheme();
  const badgeColor = getStatusBadgeColor(color, theme);
  const variant = variantsToSizeMap[size];

  return variant({
    color: badgeColor,
    borderColor: theme.status.border,
    className,
    hasBorder,
    iconName,
  });
};
